import React, { useState, useEffect, useContext } from 'react';
import {
  Alert,
  Button,
  FormGroup,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  InputGroup,
  Form,
  Row,
  Col,
  Container,
} from 'reactstrap';

import agent from '../../../agent';
import dayjs from 'dayjs';
import {
  OfficesContext,
  OfficesContextType,
} from '../../../context/ProfileContext';
import types from '../../../types/services-api';
import { FaPencilAlt, FaTimes, FaTrash } from 'react-icons/fa';
import LoadingSpinner from '../../common/LoadingSpinner';
const customParseFormat = require('dayjs/plugin/customParseFormat');
dayjs.extend(customParseFormat);

const OfficeHoursAddEditModal = (props: {
  office: types.OfficeProfileViewType;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { upsertOffice } = useContext(OfficesContext) as OfficesContextType;
  const [errorMessage, setErrorMessage] = useState('');
  const [office, setOffice] = useState(props.office);
  const [dayToEdit, setDayToEdit] = useState(-1);
  const [dayToDelete, setDayToDelete] = useState(-1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false);

  useEffect(() => {
    if (dayToDelete >= 0) {
      setDeleteModalIsOpen(true);
    }
  }, [dayToDelete]);

  function toggleIsOpen() {
    setIsOpen(!isOpen);
  }

  function addDow(e) {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage('');
    const { day, startTime, endTime } = e.target.elements;
    agent.Employer.insertOfficeHours({
      officeId: office.id,
      body: {
        day: day.value,
        openTime: dayjs(
          new Date('2011-10-10T' + startTime.value + ':00')
        ).format('HH:mm'),
        closeTime: dayjs(
          new Date('2011-10-10T' + endTime.value + ':00')
        ).format('HH:mm'),
      },
    })
      .then((res) => {
        office.hours = res.data;
        setOffice(office);
        setIsSubmitting(false);
        upsertOffice(office);
        return;
      })
      .catch((err) => {
        setIsSubmitting(false);
        setErrorMessage(err.message);
        return;
      });
  }

  function editDow(e) {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage('');
    const { editStartTime, editEndTime } = e.target.elements;
    agent.Employer.insertOfficeHours({
      officeId: office.id,
      body: {
        day: dayToEdit,
        openTime: dayjs(
          new Date('2011-10-10T' + editStartTime.value + ':00')
        ).format('HH:mm'),
        closeTime: dayjs(
          new Date('2011-10-10T' + editEndTime.value + ':00')
        ).format('HH:mm'),
      },
    })
      .then((res) => {
        office.hours = res.data;
        setOffice(office);
        upsertOffice(office);
        setDayToEdit(-1);
        setIsSubmitting(false);
        return;
      })
      .catch((err) => {
        setErrorMessage(err.message);
        setDayToEdit(-1);
        setIsSubmitting(false);
        return;
      });
  }

  function confirmedDeleteDow() {
    agent.Employer.deleteOfficeHours({ officeId: office.id, dow: dayToDelete })
      .then((res) => {
        office.hours = office.hours.filter((day) => day.day !== dayToDelete);
        setOffice(office);
        upsertOffice(office);
        setDayToDelete(-1);
        setDeleteModalIsOpen(false);
      })
      .catch((err) => {
        if (err.data.error.code === 404) {
          office.hours = office.hours.filter((day) => day.day !== dayToDelete);
          setOffice(office);
          upsertOffice(office);
          setDayToDelete(-1);
          setDeleteModalIsOpen(false);
        }
      });
  }

  return (
    <>
      <Button color="primary" outline onClick={toggleIsOpen}>
        Edit Office Hours
      </Button>
      <Modal isOpen={isOpen} toggle={toggleIsOpen}>
        <ModalHeader toggle={toggleIsOpen}>Add/Edit Office Hours</ModalHeader>
        <ModalBody>
          {isSubmitting === true ? (
            <LoadingSpinner />
          ) : (
            <Container fluid>
              <p className="text-secondary mb-0">Office Hours</p>
              {office.hours.length === 0 && <p>None Available</p>}
              {office.hours.map((day) => {
                return (
                  <div key={`office-hours-${day.day}`}>
                    <div className="d-flex align-items-center">
                      <span className="fw-bold text-muted me-2">
                        {dayjs().day(day.day).format('dddd')}:{' '}
                      </span>
                      {dayToEdit !== day.day ? (
                        <>
                          {dayjs(day.openTime, 'HH:mm').format('h:mma')} -{' '}
                          {dayjs(day.closeTime, 'HH:mm').format('h:mma')}
                          <Button
                            color="link"
                            onClick={() => setDayToEdit(day.day)}
                          >
                            <FaPencilAlt title="edit office" />
                          </Button>
                          <Button
                            color="link"
                            onClick={() => setDayToDelete(day.day)}
                            className="link-danger"
                          >
                            <FaTrash title="remove office" />
                          </Button>
                        </>
                      ) : (
                        <Form
                          id="edit-office-hours-form"
                          method="POST"
                          onSubmit={editDow}
                          onReset={() => setDayToEdit(-1)}
                        >
                          <InputGroup>
                            <FormGroup floating>
                              <Input
                                type="time"
                                name="editStartTime"
                                defaultValue={day.openTime.toString()}
                                required
                              />
                              <Label for="startTime">Start Time</Label>
                            </FormGroup>
                            <FormGroup floating>
                              <Input
                                type="time"
                                name="editEndTime"
                                defaultValue={day.closeTime.toString()}
                                dateFormat="h:mm aa"
                                required
                              />
                              <Label for="endTime">End Time</Label>
                            </FormGroup>
                            <Button
                              color="light"
                              type="reset"
                              form="edit-office-hours-form"
                              className="mb-3"
                            >
                              <FaTimes />
                            </Button>
                            <Button
                              color="primary"
                              type="submit"
                              form="edit-office-hours-form"
                              className="mb-3"
                            >
                              Save
                            </Button>
                          </InputGroup>
                        </Form>
                      )}
                    </div>
                  </div>
                );
              })}
              <hr />
              <Row>
                <Col xs="auto" className="flex-grow-1">
                  <h6>Add additional hours</h6>
                  <Form
                    id="add-office-hours-form"
                    method="POST"
                    onSubmit={addDow}
                  >
                    <InputGroup>
                      <FormGroup floating>
                        <Input
                          type="select"
                          name="day"
                          id="day"
                          defaultValue={1}
                        >
                          {[
                            'Sunday',
                            'Monday',
                            'Tuesday',
                            'Wednesday',
                            'Thursday',
                            'Friday',
                            'Saturday',
                          ].map((day, index) => {
                            if (
                              office.hours.findIndex(
                                (obj) => obj.day === index
                              ) < 0
                            ) {
                              return (
                                <option key={day} value={index}>
                                  {day}
                                </option>
                              );
                            }
                          })}
                        </Input>
                        <Label for="day">Day of Week</Label>
                      </FormGroup>
                      <FormGroup floating>
                        <Input type="time" name="startTime" required />
                        <Label for="startTime">Start Time</Label>
                      </FormGroup>
                      <FormGroup floating>
                        <Input
                          type="time"
                          name="endTime"
                          //dateFormat="h:mm aa"
                          required
                        />
                        <Label for="endTime">End Time</Label>
                      </FormGroup>
                      <Button
                        color="primary"
                        type="submit"
                        form="add-office-hours-form"
                        className="mb-3"
                      >
                        Add
                      </Button>
                    </InputGroup>
                  </Form>
                </Col>
              </Row>
              <Alert isOpen={!!errorMessage} color="danger">
                {errorMessage}
              </Alert>
            </Container>
          )}
        </ModalBody>
      </Modal>

      <Modal isOpen={deleteModalIsOpen}>
        <ModalHeader>Warning</ModalHeader>
        <ModalBody>
          <p>Are you sure you want to remove this day?</p>
        </ModalBody>
        <ModalFooter className="d-flex justify-content-between">
          <Button onClick={() => setDeleteModalIsOpen(false)} color="link">
            Cancel
          </Button>
          <Button onClick={confirmedDeleteDow} color="primary">
            Remove
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default OfficeHoursAddEditModal;
